import { useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import { useThreeScene, fitSceneCameraToBox } from '@/features/scene/hooks/useThreeScene';
import { useLighting } from '@/features/scene/hooks/useLighting';
import { useRenderLoop, type LoopHook } from '@/features/scene/hooks/useRenderLoop';
import { disposeCanonicalEntityPreviewGroup } from '@/features/scene/hooks/useGltfLoader';
import { createMeshoptGltfLoader } from '@/features/scene/lib/meshoptGltfLoader';

export type PublicNativeDescriptor = {
  schema: 'holzcom.public-native-model.v2'; modelId: string; sha256: string;
  byteLength: number; partCount: number; primitiveCount: number; mediaType: 'model/gltf-binary'; url: string;
};

export function parsePublicNative(value: unknown, modelId: string): PublicNativeDescriptor {
  if (!value || typeof value !== 'object' || Array.isArray(value)) throw new Error('Некорректный формат модели.');
  const row = value as Record<string, unknown>;
  const keys = ['schema', 'modelId', 'sha256', 'byteLength', 'partCount', 'primitiveCount', 'mediaType', 'url'];
  if (Object.keys(row).length !== keys.length || keys.some((key) => !(key in row))
    || row.schema !== 'holzcom.public-native-model.v2' || row.modelId !== modelId
    || typeof row.sha256 !== 'string' || !/^[a-f0-9]{64}$/.test(row.sha256)
    || !Number.isSafeInteger(row.byteLength) || Number(row.byteLength) < 28
    || !Number.isSafeInteger(row.partCount) || Number(row.partCount) < 1
    || !Number.isSafeInteger(row.primitiveCount) || Number(row.primitiveCount) < Number(row.partCount)
    || row.mediaType !== 'model/gltf-binary'
    || row.url !== `/api/public/model-share/glb?v=${row.sha256}`) throw new Error('Некорректная привязка модели.');
  return row as PublicNativeDescriptor;
}

export async function verifyPublicBytes(bytes: ArrayBuffer, descriptor: PublicNativeDescriptor): Promise<void> {
  const hash = Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', bytes)))
    .map((value) => value.toString(16).padStart(2, '0')).join('');
  if (bytes.byteLength !== descriptor.byteLength || hash !== descriptor.sha256) {
    throw new Error('Контрольная сумма модели не совпала.');
  }
}

export function verifyPublicComposition(actualMeshes: number,
  nodes: Array<{ mesh?: number }> | undefined,
  sourceMeshes: Array<{ primitives: unknown[] }> | undefined,
  descriptor: PublicNativeDescriptor): void {
  const meshNodes = nodes?.filter((node) => Number.isSafeInteger(node.mesh)) ?? [];
  if (!sourceMeshes || meshNodes.length !== descriptor.partCount
    || meshNodes.reduce((sum, node) => sum + (sourceMeshes[node.mesh!]?.primitives.length ?? 0), 0)
      !== descriptor.primitiveCount
    || actualMeshes !== descriptor.primitiveCount) throw new Error('Состав модели не совпал.');
}

export function PublicNativeCanvas({ descriptor, token }: { descriptor: PublicNativeDescriptor; token: string }) {
  const ref = useRef<HTMLDivElement | null>(null);
  const ctx = useThreeScene(ref);
  const [status, setStatus] = useState('Загружаю 3D-модель…');
  const [error, setError] = useState(false);
  const hooks = useMemo<LoopHook[]>(() => [], []);
  useLighting(ctx, 'showroom', 1);
  useRenderLoop(ctx, hooks);
  useEffect(() => {
    if (!ctx) return;
    const abort = new AbortController();
    let disposed = false;
    let group: THREE.Group | null = null;
    setError(false);
    setStatus('Загружаю 3D-модель…');
    void (async () => {
      const response = await fetch(descriptor.url, { headers: { 'X-Holzcom-Share': token },
        credentials: 'omit', referrerPolicy: 'no-referrer', signal: abort.signal, cache: 'no-store' });
      if (!response.ok) throw new Error(response.status === 401 ? 'Ссылка отозвана или истекла.' : 'Модель недоступна.');
      const bytes = await response.arrayBuffer();
      await verifyPublicBytes(bytes, descriptor);
      if (disposed) return;
      const gltf = await createMeshoptGltfLoader().parseAsync(bytes, '');
      if (disposed) { disposeCanonicalEntityPreviewGroup(gltf.scene); return; }
      group = gltf.scene;
      let meshes = 0;
      group.traverse((object) => { if (object instanceof THREE.Mesh) meshes += 1; });
      const nodes = gltf.parser.json.nodes as Array<{ mesh?: number }> | undefined;
      const sourceMeshes = gltf.parser.json.meshes as Array<{ primitives: unknown[] }> | undefined;
      verifyPublicComposition(meshes, nodes, sourceMeshes, descriptor);
      const bounds = new THREE.Box3().setFromObject(group);
      if (bounds.isEmpty() || ![...bounds.min.toArray(), ...bounds.max.toArray()].every(Number.isFinite)) {
        throw new Error('Границы модели некорректны.');
      }
      ctx.scene.add(group);
      fitSceneCameraToBox(ctx, bounds, 1.2);
      setStatus('Потяните мышью для обзора · колесо — масштаб');
    })().catch((reason: unknown) => {
      if (disposed) return;
      if (group) { ctx.scene.remove(group); disposeCanonicalEntityPreviewGroup(group); group = null; }
      setError(true);
      setStatus(reason instanceof Error ? reason.message : 'Не удалось открыть модель.');
    });
    return () => {
      disposed = true; abort.abort();
      if (group) { ctx.scene.remove(group); disposeCanonicalEntityPreviewGroup(group); }
    };
  }, [ctx, descriptor, token]);
  return <div className="absolute inset-x-0 bottom-0 top-12">
    <div ref={ref} className="absolute inset-0" />
    <div role={error ? 'alert' : 'status'} className="absolute bottom-3 left-3 rounded border border-border bg-surface px-3 py-2 text-sm">{status}</div>
  </div>;
}
